interface ICollectorConfig {
  [0]: string;
  [1]: ((payload: string) => void) | string | number | object;
}

interface ICollector extends Array<ICollectorConfig> {
  [index: number]: ICollectorConfig;
  push: (conf: ICollectorConfig) => number;
}

interface ICollectorCustomIdentifiers {
  sid: string;
  tid: string;
}

interface ILogger {
  error(msg: string): void;
  info?(msg: string): void;
}

interface IClientProfilerOptions {
  filename: string;
  generateTransactionId(): string;
  host: string;
  loader(url: string): Promise<void>;
  logger: ILogger;
  siteKey: string;
}

export interface IClientProfile {
  payload: string;
  transactionId: string;
}

// eslint-disable-next-line no-shadow
enum ECollectorCommand {
  CUSTOM_IDENTIFIERS = 'ci',
  GENERATE_PAYLOAD = 'cfp',
  RUN = 'run',
}

declare global {
  interface Window {
    _cc: ICollector;
  }
}

const noop = (): void => undefined;

export default class ClientProfiler {
  private collector: ICollector;
  private timePayloadRequested = -1;
  private timeCollectorRequested = -1;
  private host: string;
  private filename: string;
  private siteKey: string;
  private transactionId = '';
  private payload = '';
  private logger: ILogger;
  private isLoaded = false;
  private generateTransactionId?(): string;
  private loader?(url: string): Promise<void>;

  private static getElapsedTime(time: number): number {
    return Math.abs(time - Date.now());
  }

  private static getTruncatedString(str: string): string {
    return str ? `${str.substring(0, 20)}...` : '';
  }

  private get timeSincePayloadRequested(): number {
    return ClientProfiler.getElapsedTime(this.timePayloadRequested);
  }

  private get timeSinceCollectorRequested(): number {
    return ClientProfiler.getElapsedTime(this.timeCollectorRequested);
  }

  constructor({
    siteKey,
    host,
    filename,
    loader,
    generateTransactionId,
    logger = {
      info: noop,
      error: noop,
    },
  }: IClientProfilerOptions) {
    logger.info?.('Instantiating ClientProfiler');

    if (Array.isArray(window._cc)) {
      this.collector = window._cc;
    } else {
      this.collector = [] as ICollectorConfig[];
      window._cc = this.collector;
    }

    this.setPayload = this.setPayload.bind(this);

    this.host = host;
    this.filename = filename;
    this.siteKey = siteKey;
    this.logger = logger;
    this.loader = loader;
    this.generateTransactionId = generateTransactionId;
    this.setTransactionId(generateTransactionId());
    this.preConfigureCollector();
  }

  private preConfigureCollector(): void {
    this.pushCustomIdentifiers({
      sid: this.siteKey,
      tid: this.transactionId,
    });

    this.run();
  }

  private initTimePayloadRequested(): void {
    this.timePayloadRequested = Date.now();
  }

  private initTimeCollectorRequested(): void {
    this.timeCollectorRequested = Date.now();
  }

  private setTransactionId(tid: string): void {
    this.logger.info?.(`Setting transactionId: ${tid}`);
    this.transactionId = tid;
  }

  private setPayload(payload: string): void {
    this.logger.info?.(`Payload generated in ${this.timeSincePayloadRequested}ms`);
    this.payload = payload;
  }

  private async fetchCollector(): Promise<void> {
    if (this.isLoaded) {
      return;
    }

    this.logger.info?.('Downloading collector');
    this.initTimeCollectorRequested();
    await this.loader?.(`${this.host}/${this.filename}`);
    this.logger.info?.(`Downloaded collector in ${this.timeSinceCollectorRequested}ms`);
    this.isLoaded = true;
  }

  private pushCustomIdentifiers(customIdentifiers: ICollectorCustomIdentifiers): void {
    this.collector.push([ECollectorCommand.CUSTOM_IDENTIFIERS, customIdentifiers]);
  }

  private generatePayload(): void {
    this.logger.info?.('Generating payload');
    this.initTimePayloadRequested();
    this.collector.push([ECollectorCommand.GENERATE_PAYLOAD, this.setPayload]);
  }

  private run(): void {
    this.collector.push([ECollectorCommand.RUN, this.host]);
  }

  async init(): Promise<void> {
    await this.fetchCollector();

    if (this.payload) {
      return;
    }
    this.generatePayload();
  }

  renew(): void {
    const transactionId = this.generateTransactionId?.();
    if (transactionId) {
      this.setTransactionId(transactionId);
      this.pushCustomIdentifiers({ tid: transactionId, sid: this.siteKey });
      this.run();
      this.generatePayload();
    }
  }

  getClientProfile(): IClientProfile {
    if (!this.transactionId || !this.payload) {
      const truncatedPayload = ClientProfiler.getTruncatedString(this.payload);
      this.logger.error(
        `Missing required data: {transactionId: "${this.transactionId}", payload: "${truncatedPayload}", timeSinceCollectorRequested: "${this.timeSinceCollectorRequested}", timeSincePayloadRequested: "${this.timeSincePayloadRequested}"}`,
      );
    }

    return {
      transactionId: this.transactionId || '',
      payload: this.payload || '',
    };
  }
}
